// SAM-199 follow-up — service-booking date/guest readiness.
//
// Decides whether the shared HowLongControl's current selection is enough to
// show the SERVICE availability picker (ServiceBookingSelector), and picks the
// fallback message when it isn't. Pure/dependency-free so the one-day vs
// multi-day branches — and the message choice — can be unit tested directly.
// See serviceDateReadiness.test.ts.
//
// Why a SERVICE booking never accepts a date RANGE today (investigated for
// SAM-199 "the flows don't connect" report): SAM's own booking write path
// (sam-lambda-functions functions/booking/lambda_function.py::create_booking,
// ~line 2213 `reservation_end = body.get('reservation_end', reservation_start)`
// and ~line 2289-2295) reduces a 'day'-type service booking to exactly ONE
// calendar date — day_schedule.py's split_reservation_date() parses a single
// YYYY-MM-DD out of `reservation_start`, and build_day_reservation_window()
// builds an availability/capacity window for that one day only. `reservation_end`
// is accepted on the wire but never read for day-type services.
// `service.settings.min_days`/`max_days` ARE synced one-way to the booking
// engine (engine_sync.py, for the engine's own future scheduling) but are never
// read back by create_booking, so nothing in SAM enforces — or makes USE of —
// them for an actual multi-day reservation. Until SAM grows a real "book N
// dates in one request" path, HowLongControl's "Multiple days" mode must not be
// offered while booking a SERVICE (see AccessForm's `restrictToOneDay` prop to
// HowLongControl, driven by this module). This file documents and tests that
// decision so a future "just wire up the date range" change doesn't silently
// reintroduce the dead end without re-checking the backend first.

export type ServiceDurationSelection =
  | { mode?: 'one'; date?: string }
  | { mode: 'multiple'; startDate?: string; endDate?: string };

export interface ServiceDateReadiness {
  ready: boolean;
  /** null when ready === true */
  reason: string | null;
}

export const ONE_DAY_NOT_READY_REASON =
  'Pick a day and confirm Adults/Children in "How long?" above to see this service\'s availability.';

export const MULTI_DAY_UNSUPPORTED_REASON =
  'Service bookings are for a single day — switch to "One day" above to see this service\'s availability.';

/** Inclusive day count between two ISO date(time) strings, or null if either is missing/invalid. */
function inclusiveDayCount(startDate?: string, endDate?: string): number | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
}

/**
 * `minDays`/`maxDays` come from `service.settings.min_days`/`max_days`. They are
 * passed through purely so the fallback message can say something specific
 * about the selected range (e.g. "this service allows up to N days"). They do
 * NOT change the ready/not-ready outcome: a multi-day selection is never ready
 * for a SERVICE booking today, regardless of whether it happens to fall inside
 * the service's own configured min/max — see the module header for why.
 */
export function evaluateServiceDateReadiness(
  duration: ServiceDurationSelection | null | undefined,
  options: { minDays?: number; maxDays?: number } = {}
): ServiceDateReadiness {
  const mode = duration?.mode ?? 'one';

  if (mode === 'one') {
    const date = duration && 'date' in duration ? duration.date : undefined;
    return date ? { ready: true, reason: null } : { ready: false, reason: ONE_DAY_NOT_READY_REASON };
  }

  // mode === 'multiple'
  const startDate = duration && 'startDate' in duration ? duration.startDate : undefined;
  const endDate = duration && 'endDate' in duration ? duration.endDate : undefined;
  if (!startDate || !endDate) {
    return { ready: false, reason: ONE_DAY_NOT_READY_REASON };
  }

  const rangeDays = inclusiveDayCount(startDate, endDate);
  const { minDays, maxDays } = options;

  if (rangeDays !== null && typeof maxDays === 'number' && maxDays > 0 && rangeDays > maxDays) {
    return {
      ready: false,
      reason: `${MULTI_DAY_UNSUPPORTED_REASON} (this service allows up to ${maxDays} day${maxDays === 1 ? '' : 's'} per booking, and only one date can be booked at a time today.)`,
    };
  }
  if (rangeDays !== null && typeof minDays === 'number' && minDays > 0 && rangeDays < minDays) {
    return {
      ready: false,
      reason: `${MULTI_DAY_UNSUPPORTED_REASON} (this service requires at least ${minDays} day${minDays === 1 ? '' : 's'}, and only one date can be booked at a time today.)`,
    };
  }

  // Within (or unknown against) the service's own min/max — still not ready:
  // SAM cannot write a multi-day reservation regardless of that config.
  return { ready: false, reason: MULTI_DAY_UNSUPPORTED_REASON };
}
